import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { AdminGalleryAlbum, AdminGalleryPhoto } from "@tih/shared";
import { ApiError, adminUpdateGalleryAlbum, fetchGalleryAlbumZipBlob, triggerBrowserFileDownload } from "../api/client";
import {
  adminUploadGalleryPhotosWithProgress,
  adminUploadGalleryZipWithProgress,
  type GalleryUploadProgress,
} from "../api/gallery-upload";

export type GalleryJobKind = "upload" | "download";

export type GalleryUploadPhase =
  | "uploading"
  | "extracting"
  | "processing"
  | "preparing"
  | "complete"
  | "error";

export type GalleryUploadJob = {
  id: string;
  kind: GalleryJobKind;
  albumId: number;
  albumTitle: string;
  albumSlug: string;
  albumPublished: boolean;
  fileName: string;
  phase: GalleryUploadPhase;
  uploadPercent?: number;
  current?: number;
  total?: number;
  skipped?: number;
  photoCount?: number;
  previewPhotos: AdminGalleryPhoto[];
  downloadUrl?: string;
  downloadFilename?: string;
  error?: string;
  hidden: boolean;
};

type GalleryUploadContextValue = {
  jobs: GalleryUploadJob[];
  startUpload: (album: AdminGalleryAlbum, files: FileList | File[]) => void;
  startDownloadZip: (album: AdminGalleryAlbum) => void;
  hideJob: (jobId: string) => void;
  removeJob: (jobId: string) => void;
  publishAlbum: (jobId: string) => Promise<void>;
  onAlbumUploadComplete: (listener: (albumId: number) => void) => () => void;
};

const GalleryUploadContext = createContext<GalleryUploadContextValue | null>(null);

function isZipFile(file: File): boolean {
  const name = file.name.toLowerCase();
  return (
    name.endsWith(".zip") ||
    file.type === "application/zip" ||
    file.type === "application/x-zip-compressed"
  );
}

function nextJobId(prefix: "gallery-upload" | "gallery-download"): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function applyProgress(job: GalleryUploadJob, event: GalleryUploadProgress): GalleryUploadJob {
  if (event.phase === "uploading") {
    return { ...job, phase: "uploading", uploadPercent: event.percent, fileName: event.fileName };
  }
  if (event.phase === "extracting") {
    return { ...job, phase: "extracting", fileName: event.fileName };
  }
  if (event.phase === "processing") {
    return {
      ...job,
      phase: "processing",
      current: event.current,
      total: event.total,
      skipped: event.skipped,
      fileName: event.fileName,
    };
  }
  if (event.phase === "complete") {
    return {
      ...job,
      phase: "complete",
      fileName: event.fileName,
      photoCount: event.photoCount,
      skipped: event.skipped,
      previewPhotos: event.photos.slice(0, 8),
      current: event.photoCount,
      total: event.total,
    };
  }
  if (event.phase === "error") {
    return { ...job, phase: "error", error: event.error, fileName: event.fileName };
  }
  return job;
}

export function GalleryUploadProvider({ children }: { children: ReactNode }) {
  const [jobs, setJobs] = useState<GalleryUploadJob[]>([]);
  const completeListeners = useRef(new Set<(albumId: number) => void>());

  const updateJob = useCallback((jobId: string, patch: Partial<GalleryUploadJob>) => {
    setJobs((prev) => prev.map((j) => (j.id === jobId ? { ...j, ...patch } : j)));
  }, []);

  const patchJobFromEvent = useCallback((jobId: string, event: GalleryUploadProgress) => {
    setJobs((prev) =>
      prev.map((j) => (j.id === jobId ? applyProgress(j, event) : j)),
    );
  }, []);

  const notifyComplete = useCallback((albumId: number) => {
    for (const listener of completeListeners.current) {
      listener(albumId);
    }
  }, []);

  const runDownloadJob = useCallback(
    async (jobId: string, albumId: number) => {
      try {
        const { blob, filename } = await fetchGalleryAlbumZipBlob(albumId);
        const url = URL.createObjectURL(blob);
        triggerBrowserFileDownload(url, filename);
        updateJob(jobId, {
          phase: "complete",
          fileName: filename,
          downloadUrl: url,
          downloadFilename: filename,
        });
      } catch (e) {
        const message = e instanceof ApiError ? e.message : String(e);
        updateJob(jobId, { phase: "error", error: message });
      }
    },
    [updateJob],
  );

  const runJob = useCallback(
    async (jobId: string, albumId: number, zips: File[], imageFiles: File[]) => {
      try {
        for (const zip of zips) {
          await adminUploadGalleryZipWithProgress(albumId, zip, (event) => {
            patchJobFromEvent(jobId, event);
          });
        }
        if (imageFiles.length > 0) {
          await adminUploadGalleryPhotosWithProgress(albumId, imageFiles, (event) => {
            patchJobFromEvent(jobId, event);
          });
        }
        notifyComplete(albumId);
      } catch (e) {
        const message = e instanceof ApiError ? e.message : String(e);
        updateJob(jobId, { phase: "error", error: message });
      }
    },
    [notifyComplete, patchJobFromEvent, updateJob],
  );

  const startUpload = useCallback(
    (album: AdminGalleryAlbum, files: FileList | File[]) => {
      const list = Array.from(files);
      const zips = list.filter(isZipFile);
      const imageFiles = list.filter(
        (f) =>
          !isZipFile(f) &&
          (f.type.startsWith("image/") || /\.(jpe?g|png|webp|avif|heic|heif)$/i.test(f.name)),
      );
      if (zips.length === 0 && imageFiles.length === 0) return;

      const fileName =
        zips.length === 1 && imageFiles.length === 0
          ? zips[0]!.name
          : imageFiles.length === 1 && zips.length === 0
            ? imageFiles[0]!.name
            : `${zips.length + imageFiles.length} files`;

      const jobId = nextJobId("gallery-upload");
      const job: GalleryUploadJob = {
        id: jobId,
        kind: "upload",
        albumId: album.id,
        albumTitle: album.title,
        albumSlug: album.slug,
        albumPublished: album.published,
        fileName,
        phase: "uploading",
        uploadPercent: 0,
        previewPhotos: [],
        hidden: false,
      };

      setJobs((prev) => [...prev, job]);
      void runJob(jobId, album.id, zips, imageFiles);
    },
    [runJob],
  );

  const startDownloadZip = useCallback(
    (album: AdminGalleryAlbum) => {
      const jobId = nextJobId("gallery-download");
      const job: GalleryUploadJob = {
        id: jobId,
        kind: "download",
        albumId: album.id,
        albumTitle: album.title,
        albumSlug: album.slug,
        albumPublished: album.published,
        fileName: `${album.slug}-watermarked.zip`,
        phase: "preparing",
        previewPhotos: [],
        hidden: false,
      };

      setJobs((prev) => [...prev, job]);
      void runDownloadJob(jobId, album.id);
    },
    [runDownloadJob],
  );

  const hideJob = useCallback((jobId: string) => {
    updateJob(jobId, { hidden: true });
  }, [updateJob]);

  const removeJob = useCallback((jobId: string) => {
    setJobs((prev) => {
      const job = prev.find((j) => j.id === jobId);
      if (job?.downloadUrl) URL.revokeObjectURL(job.downloadUrl);
      return prev.filter((j) => j.id !== jobId);
    });
  }, []);

  const publishAlbum = useCallback(
    async (jobId: string) => {
      const job = jobs.find((j) => j.id === jobId);
      if (!job) return;
      await adminUpdateGalleryAlbum(job.albumId, { published: true });
      updateJob(jobId, { albumPublished: true });
    },
    [jobs, updateJob],
  );

  const onAlbumUploadComplete = useCallback((listener: (albumId: number) => void) => {
    completeListeners.current.add(listener);
    return () => {
      completeListeners.current.delete(listener);
    };
  }, []);

  const value = useMemo(
    () => ({
      jobs,
      startUpload,
      startDownloadZip,
      hideJob,
      removeJob,
      publishAlbum,
      onAlbumUploadComplete,
    }),
    [jobs, startUpload, startDownloadZip, hideJob, removeJob, publishAlbum, onAlbumUploadComplete],
  );

  return (
    <GalleryUploadContext.Provider value={value}>{children}</GalleryUploadContext.Provider>
  );
}

export function useGalleryUpload(): GalleryUploadContextValue {
  const ctx = useContext(GalleryUploadContext);
  if (!ctx) {
    throw new Error("useGalleryUpload must be used within GalleryUploadProvider");
  }
  return ctx;
}
